import { useSelector, useDispatch } from "react-redux";
import { Typography } from "@mui/material";
import AppState from "../../store/AppState";
import { selectIsModalOpen } from "../../store/modal/ModalSelectors";
import { setModalState } from "../../store/modal/ModalActions";
import { useIntl } from "react-intl";
import { useCallback } from "react";
import CustomDialog from "../common/DialogContainer";
import ButtonComponent from "../common/ButtonComponent";

import { makeStyles, createStyles } from "@material-ui/core";

const useStyles = makeStyles(() =>
  createStyles({
    btnGroup: {
      textAlign: "end",
      margin: "20px 20px 10px",
      width: "100%",
    },
    divMessage: {
      marginTop: "5px",
    },
  }),
);

const ClipBoardActionModal = (props: {
  handleAdd: () => void;
  currentAction?: string | undefined | null;
  invalidProductCodes?: string[];
}) => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const classes = useStyles();
  const isModalOpen = useSelector((state: AppState) =>
    selectIsModalOpen(state, "ClipBoardConfirmParNumbersModal"),
  );

  const handleClose = () => {
    dispatch(setModalState("ClipBoardConfirmParNumbersModal", false));
  };

  const getCurrentAction = useCallback(() => {
    switch (props.currentAction) {
      case "AddProductsModal":
        return "add";
      case "RemoveProductsModal":
        return "remove";
      case "ReplaceProductsModal":
        return "replace";
      case "MultipleStoreModal":
        return "publish/unpublish";
      default:
        return "";
    }
  }, [props.currentAction]);

  const getModalTitle = useCallback(() => {
    switch (props.currentAction) {
      case "AddProductsModal":
        return intl.formatMessage({
          id: "addProductsModal.header",
          defaultMessage: "Add Products",
        });
      case "RemoveProductsModal":
        return intl.formatMessage({
          id: "confirmRemoveProducts.header",
          defaultMessage: "Remove Products",
        });
      case "ReplaceProductsModal":
        return intl.formatMessage({
          id: "replaceProductsModal.header",
          defaultMessage: "Replace Products",
        });
      case "MultipleStoreModal":
        return intl.formatMessage({
          id: "multipleStoreModal.header",
          defaultMessage: "Publish / Unpublish Products",
        });
      default:
        return intl.formatMessage({
          id: "confirmPartNumber.header",
          defaultMessage: "Confirm Part Numbers",
        });
    }
  }, [props.currentAction, intl]);

  const clipBoardActionModalTitle = () => {
    return <Typography variant="inherit">{getModalTitle()}</Typography>;
  };

  const clipBoardActionModalContent = () => {
    return (
      <div className={classes.divMessage}>
        <Typography variant="subtitle1">
          {intl.formatMessage(
            {
              id: "confirmPartNumber.instruction",
              defaultMessage:
                "Invalid Part Number(s): {commaSeparatedListOfInvalidPartnumbers}.  Click proceed to {action} the rest of the part numbers?",
            },
            {
              commaSeparatedListOfInvalidPartnumbers:
                props.invalidProductCodes?.join(", "),
              action: getCurrentAction(),
            },
          )}
        </Typography>
      </div>
    );
  };

  const dialogActions = () => {
    return (
      <div className={classes.btnGroup}>
        <ButtonComponent
          color="secondaryButtonColorCTABlue"
          variant="outlined"
          onClick={handleClose}
        >
          {intl.formatMessage({
            id: "general.cancel",
            defaultMessage: "Cancel",
          })}
        </ButtonComponent>
        <ButtonComponent
          color="primary"
          variant="contained"
          onClick={() => {
            props.handleAdd();
            handleClose();
          }}
        >
          {intl.formatMessage({
            id: "general.Proceed",
            defaultMessage: "Proceed",
          })}
        </ButtonComponent>
      </div>
    );
  };

  return (
    <CustomDialog
      open={isModalOpen}
      onClose={() => handleClose()}
      title={clipBoardActionModalTitle()}
      actions={dialogActions()}
      maxWidth={"md"}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: "rgba(0, 0, 0, 0.1)",
          },
        },
      }}
    >
      {clipBoardActionModalContent()}
    </CustomDialog>
  );
};

export default ClipBoardActionModal;
